<template>
    <div
        :class="[ 'select-with-search', {
            'select-with-search--active': active,
            'select-with-search--no-placeholder': !!searchValue,
        }]"
    >
        <div class="select-with-search__textfield-wrapper">
            <input
                ref="input"
                v-model="searchValue"
                type="search"
                :id="`search-${name}`"
                :name="`search-${name}`"
                class="select-with-search__textfield"
                placeholder=" "
                autocomplete="nope"
                @input="updateResults"
                @click="$emit('click')"
                @focus="openItems"
                @focusout="focusOut"
                @blur="blur"
            >
            <p class="select-with-search__textfield-placeholder">
                {{ placeholder }}
            </p>
            <div
                v-show="!!searchValue && !showExpandIcon"
                class="select-with-search__clear-button"
                @click="clearInput"
            >
                <svg viewBox="0 0 80 80">
                    <polygon points="53.5,50 43.5,40 53.5,30 50,26.5 40,36.5 30,26.5 26.5,30 36.5,40 26.5,50 30,53.5 40,43.5 50,53.5"/>
                </svg>
            </div>
            <div
                v-show="showExpandIcon"
                class="select-with-search__expand-icon"
                @click="clearInput"
            >
                <svg viewBox="0 0 80 80">
                    <path
                        class="arrow select-arrow-3"
                        d="M41.7,50.6c4.3-4.7,8.5-9.5,12.8-14.2c1.8-2-1.1-4.9-2.9-2.9c-3.8,4.2-7.6,8.5-11.4,12.7c-3.9-4.2-7.9-8.5-11.8-12.7 c-1.8-1.9-4.6,1-2.9,2.9c4.4,4.8,8.8,9.5,13.2,14.3C39.5,51.4,40.9,51.4,41.7,50.6z"
                    />
                </svg>
            </div>
        </div>

        <div
            v-if="active && options.length > 1"
            ref="list"
            :class="listClass"
            tabindex="-1"
            @scroll="checkShadows"
        >
            <ul
                v-for="(group, groupIndex) in options"
                :key="groupIndex"
                class="select-with-search__list-group"
            >
                <li class="select-with-search__list-group-title">
                    {{ group.name }}
                </li>
                <li
                    v-for="(option, optionIndex) in group.options"
                    :key="optionIndex"
                    :data-key="option.key"
                    :data-showstopper="option.showstopper"
                    :data-index="startOptionIndex(groupIndex) + optionIndex"
                    :class="{
                        'select-with-search__item': true,
                        'select-with-search__item--active': (startOptionIndex(groupIndex) + optionIndex) === focusedItemIndex,
                        'select-with-search__item--button': option.button,

                    }"
                >
                    <img
                        v-if="option.icon"
                        class="select-with-search__item-icon"
                        :src="option.icon"
                    />
                    <p class="select-with-search__item-text">
                        {{ option.text || option.value }}
                    </p>
                </li>
            </ul>
        </div>
        <ul
            v-else-if="active"
            ref="list"
            :class="listClass"
            tabindex="-1"
            @scroll="checkShadows"
        >
            <li
                v-for="(option, index) in options[0].options"
                :key="index"
                :data-key="option.key"
                :data-showstopper="option.showstopper"
                :class="{
                    'select-with-search__item': true,
                    'select-with-search__item--active': index === focusedItemIndex,
                    'select-with-search__item--button': option.button,
                }"
            >
                <img
                    v-if="option.icon"
                    class="select-with-search__item-icon"
                    :src="option.icon"
                />
                <p class="select-with-search__item-text">
                    {{ option.text || option.value }}
                </p>
            </li>
        </ul>
    </div>
</template>

<script>
export default {
    props: {
        placeholder: {
            type: String,
            required: true,
        },

        name: {
            type: String,
            required: true,
        },

        options: {
            type: Array,
            required: true,
        },

        autocomplete: {
            type: String,
            required: false,
            default: '',
        },

        showExpandIcon: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            searchValue: '',
            active: false,
            focusedItemIndex: -1,
            listShadows: {
                top: false,
                bottom: false,
            },
        };
    },

    computed: {
        listClass() {
            let $class = 'select-with-search__list';

            if(this.listShadows.top) $class += ' select-with-search__list--top-shadow';
            if(this.listShadows.bottom) $class += ' select-with-search__list--bottom-shadow';

            return $class;
        },
    },

    created() {
        this.clickHandler();
    },

    mounted() {
        const input = this.$refs.input;
        const autoComplete = input.closest('.select-with-search');

        input.addEventListener('keydown', event => {
            const items = autoComplete.querySelectorAll('.select-with-search__item');

            if (this.active) {
                if(event.key === 'Escape') {
                    this.active = false;
                    input.blur();
                } else if (event.key === 'ArrowDown') {
                    this.focusedItemIndex = this.focusedItemIndex !== items.length - 1 ? this.focusedItemIndex + 1 : 0;
                    this.scrollToCurrentOption();
                } else if (event.key === 'ArrowUp') {
                    this.focusedItemIndex = this.focusedItemIndex !== 0 ? this.focusedItemIndex - 1 : this.focusedItemIndex = (items.length - 1);
                    this.scrollToCurrentOption();
                } else if (event.key === 'Enter' && this.focusedItemIndex > -1) {
                    const option = autoComplete.querySelectorAll('.select-with-search__item')[this.focusedItemIndex];

                    if(!option.getAttribute('data-key')) return;

                    if(!!option.dataset.showstopper && option.dataset.showstopper !== '' && option.dataset.showstopper !== 'none') {
                        this.$store.commit('dialog/show', option.dataset.showstopper);
                        this.clearInput();
                        this.active = false;
                        return;
                    }

                    // Set all the form data.
                    this.updateValue(option.getAttribute('data-key'));

                    // Close the select menu after the user selected an address.
                    this.active = false;
                    input.blur();
                } else if (event.key === 'Enter') {
                    this.active = false;
                    input.blur();
                }
            }
        });
    },

    methods: {
        startOptionIndex(groupIndex) {
            let value = 0;

            for(let i = 0; i < groupIndex; i++) {
                value += this.options[i].options.length;
            }

            return value;
        },

        clearInput() {
            this.$emit('blur');
            this.searchValue = '';
            this.updateValue('', '', true);
            this.$refs.input.focus();
            this.updateResults(true);
            this.$emit('focus');
            this.checkShadows();
        },

        scrollToCurrentOption() {
            const input = this.$refs.input;
            const container = input.closest('.select-with-search');
            const list = container.querySelector('.select-with-search__list');
            const currentItem = list.querySelectorAll('.select-with-search__item')[this.focusedItemIndex];

            list.scrollTop = currentItem.offsetTop;
        },

        openItems(event) {
            this.active = true;
            this.$emit('focus');

            setTimeout(() => {
                const input = this.$refs.input;
                if(input.selectionStart === 0) {
                    input.setSelectionRange(-1, -1);
                    input.scrollLeft = input.scrollWidth - input.clientWidth;
                }
            });

            this.$nextTick(() => {
                this.checkShadows();
            });
        },

        blur(event) {
            if(!event.relatedTarget?.classList.contains('select-with-search__list')) {
                if(this.active) {
                    this.$emit('blur');
                    this.active = false;
                    this.focusedItemIndex = 0;
                }
            }
        },

        clickHandler() {
            window.addEventListener('blur', () => {
                if(this.active) {
                    this.active = false;
                    this.focusedItemIndex = 0;
                    this.$emit('blur');
                }
            }, false);

            document.addEventListener('click', async event => {
                let item = null;

                if(!this.active) return;

                if (
                    !event.target.closest('.select-with-search') ||
                    this.$refs?.input?.closest('.select-with-search') !== event.target.closest('.select-with-search')
                ) {
                    this.active = false;
                    this.focusedItemIndex = 0;
                    this.$emit('blur');
                    return;
                }

                // Check if the click happened inside of a '.autocomplete-address__item-clickable' element.
                // If so we set that element as the item.
                if (event.target.classList.contains('select-with-search__item')) item = event.target;
                else if (event.target.closest('.select-with-search__item')) item = event.target.closest('.select-with-search__item');

                // Whenever there is no item, return.
                if (!item) {
                    // If the click happened outside of the autocomplete address, make sure the select box closes.
                    if (!event.target.closest('.select-with-search')) {
                        this.active = false;
                        this.focusedItemIndex = 0;
                        this.$emit('blur');
                    }
                    return;
                }

                if (!item.getAttribute('data-key')) {
                    this.$refs.input.focus();
                    return;
                }

                if(!!item.dataset.showstopper && item.dataset.showstopper !== '' && item.dataset.showstopper !== 'none') {
                    this.$store.commit('dialog/show', item.dataset.showstopper);
                    this.clearInput();
                    this.active = false;
                    this.$emit('blur');
                    return;
                }

                // Set all the form data.
                await this.updateValue(item.getAttribute('data-key'));

                // Close the select menu after the user selected an address.
                this.active = false;
                this.$emit('blur');
            });
        },

        updateResults(manualInput = false, clearInput = false) {
            if (clearInput) this.searchValue = '';

            this.$emit('updateResults', this.searchValue || '', manualInput);

            this.$nextTick(() => {
                this.focusedItemIndex = 0;
                this.checkShadows();
            });
        },

        async updateValue(key, manualInput = false) {
            if(key === 'manual') {
                await this.$emit('updateValue', key, 0);
                return;
            }

            if(!key) {
                this.updateResults(manualInput);
                return;
            }

            let value;

            this.options.forEach(group => {
                group.options.forEach(option => {
                    if (option.key.toString() === key.toString()) value = option.value;
                });
            });

            this.searchValue = value;
            this.updateResults(manualInput);
            await this.$emit('updateValue', key, value);
        },

        checkShadows() {
            const listElement = this.$refs.list;

            if(!listElement) return;

            this.listShadows.top = listElement.scrollTop > 20;
            this.listShadows.bottom = listElement.scrollTop < listElement.scrollHeight - listElement.clientHeight - 20 && listElement.clientHeight !== listElement.scrollHeight;
        },

        focusOut(event) {
            if (!event.relatedTarget) {
                this.active = false;
            }
        },
    },
};
</script>
